import React, { useCallback, useMemo } from "react";
import { Input } from "antd";

/**
 * SmartNumberInput
 *
 * A reusable numeric input that:
 * - Disallows invalid characters in real time
 * - Accepts pasted formatted numbers like "1,000" or "9 99" and sanitizes them
 * - Dynamically enforces min/max if provided
 * - Allows blank/null (treated as null)
 *
 * Example:
 * <SmartNumberInput min={0} max={999} placeholder="0–999 or blank" />
 */
const SmartNumberInput = React.forwardRef(
  ({ value, onChange, min, max, ...props }, ref) => {
    const displayValue = value == null ? "" : String(value);
    const maxDigits = useMemo(
      () => (typeof max === "number" ? String(max).length : undefined),
      [max]
    );

    const sanitize = (input) => {
      if (!input) return "";
      // Remove commas, spaces, and any non-digits
      return input.replace(/\D/g, "");
    };

    // Handle input change (text typed or pasted)
    const handleChange = useCallback(
      (e) => {
        const raw = sanitize(e.target.value);
        if (raw === "") return onChange?.(null);

        let num = Number(raw);

        // Apply range constraints
        if (typeof max === "number" && num > max) num = max;
        if (typeof min === "number" && num < min) num = min;

        onChange?.(num);
      },
      [min, max, onChange]
    );

    // Prevent invalid character input in real time
    const handleBeforeInput = useCallback(
      (e) => {
        const char = e.data;

        // Block non-digits (but allow null data during paste)
        if (char && !/^\d$/.test(char)) {
          e.preventDefault();
          return;
        }

        // Enforce max digit length only for manual typing
        const { value: current, selectionStart, selectionEnd } = e.target;
        const replacingLength = selectionEnd - selectionStart;
        const nextLength = current.length - replacingLength + 1;

        if (maxDigits && nextLength > maxDigits) e.preventDefault();
      },
      [maxDigits]
    );

    // Handle paste directly
    const handlePaste = useCallback(
      (e) => {
        const text = e.clipboardData.getData("text");
        const cleaned = sanitize(text);

        if (cleaned === "") {
          e.preventDefault();
          return;
        }

        let num = Number(cleaned);
        if (typeof max === "number" && num > max) num = max;
        if (typeof min === "number" && num < min) num = min;

        e.preventDefault(); // Stop the raw paste
        onChange?.(num);
      },
      [min, max, onChange]
    );

    return (
      <Input
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        inputMode="numeric"
        pattern="[0-9]*"
        {...props}
      />
    );
  }
);

SmartNumberInput.displayName = "SmartNumberInput";

export default SmartNumberInput;
